import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useAppSelector, useAppDispatch } from "../../state/store";
import { Row } from "../Row";
import { Selection, StringInput } from "../Inputs";
import { BQATabs } from "../BQATabs/BQATabs";
import { DropdownMenu } from "../Dropdown/DropdownMenu";
import { Check } from "../Check/Check";
import { PlusMinusButton } from "../PlusMinusButton";
import { SettingsProps } from "./";
import {
  arrayToEnum,
  combineBooleans,
  getShortenedBook,
  xor,
} from "../../util/generalUtil";
import { Book, MaterialSelection, VersePreset } from "../../types/material";
import "./settings.scss";
import { SET_SETTINGS } from "../../state/reducers/settings";

// This year's material
const thisYearsBooks: number[] = [3];
const giveLinks: number[] = [3];

const scrollToBook = (bookIndex: number) => {
  // Get the items to work with
  const outerItem = document.getElementById("root");
  const innerItem = document.getElementById(`book-id-${bookIndex}`);
  if (outerItem && innerItem) {
    window.scrollTo(
      0,
      innerItem.getBoundingClientRect().top -
        outerItem.getBoundingClientRect().top -
        170
    );
  }
};

export function ChapterSelector(props: SettingsProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { material } = useAppSelector((state) => state.material);
  const settings = useAppSelector((state) => state.settings);
  const [editedSettings, setEditedSettings] = useState({ ...settings });
  const { selectedChapters, myMaterial, versePresets } = editedSettings;
  const setSelectedChapters = (chapters: MaterialSelection) =>
    setEditedSettings({ ...editedSettings, selectedChapters: chapters });
  const setMyMaterial = (myMaterial: boolean[][]) =>
    setEditedSettings({ ...editedSettings, myMaterial });
  const setVersePresets = (presets: VersePreset[]) =>
    setEditedSettings({ ...editedSettings, versePresets: presets });

  const [presetIndex, setPresetIndex] = useState(-1);

  const getNewMaterialSelection = () => {
    // Possibly update my material
    const newMaterial = [...myMaterial];
    selectedChapters.forEach((book, index) => {
      book.forEach((ch, chIndex) => {
        if (!!ch && !newMaterial[index][chIndex]) {
          const newMaterialBook = [...newMaterial[index]];
          newMaterialBook[chIndex] = true;
          newMaterial[index] = newMaterialBook;
        }
      });
    });
    return newMaterial;
  };

  const [tab, setTab] = useState(1);

  useEffect(() => {
    if (tab === 0) {
      scrollToBook(thisYearsBooks[0]);
    } else {
      window.scrollTo(0, 0);
    }
  }, [tab]);

  return (
    <div className="settings-container">
      <div className="settings-top-sticky">
        <button
          className="clickable"
          style={{ marginLeft: 10, marginBottom: 20 }}
          onClick={() => {
            navigate(-1);
            dispatch({
              type: SET_SETTINGS,
              payload: {
                ...editedSettings,
                // Filter presets that have a name and at least one verse
                versePresets: versePresets.filter(
                  (preset) =>
                    !!preset.presetName &&
                    preset.material.some((book) =>
                      book.some((ch) => {
                        if (typeof ch === "boolean") {
                          return ch;
                        } else {
                          return ch.some((v) => v);
                        }
                      })
                    )
                ),
              },
            });
            props.onClickBack?.(editedSettings);
          }}
        >
          Back
        </button>
        <BQATabs
          value={tab}
          onChange={(newTab) => {
            setTab(newTab);
            if (newTab === 0) {
              setMyMaterial(getNewMaterialSelection());
            }
          }}
          tabs={[
            {
              title: "My Material",
              key: 0,
            },
            {
              title: "Current Chapters",
              key: 1,
            },
            {
              title: "Verse Presets",
              key: 2,
            },
            {
              title: "Verse Filters",
              key: 3,
            },
          ]}
        />
        {tab === 0 && giveLinks.length > 0 && <BookQuickLinks />}
      </div>
      {tab === 0 && (
        <ChapterList
          allowVerseSelection={false}
          selected={myMaterial}
          onChange={(newMaterial) => {
            const newSettings = {
              ...editedSettings,
              myMaterial: newMaterial as boolean[][],
            };
            const newChapters = [...selectedChapters];
            newMaterial.forEach((book, index) => {
              book.forEach((chapter, chIndex) => {
                if (!chapter && !!newChapters[index][chIndex]) {
                  const newBook = [...newChapters[index]];
                  newBook[chIndex] = false;
                  newChapters[index] = newBook;
                }
              });
            });
            newSettings.selectedChapters = newChapters;
            setEditedSettings(newSettings);
          }}
          key="my-material"
        />
      )}
      {tab === 1 && (
        <>
          <div
            style={{ position: "relative", marginBottom: 10, minHeight: 15 }}
          >
            {versePresets.length > 0 && (
              <DropdownMenu
                items={versePresets.map((preset) => ({
                  title: preset.presetName,
                  onClick: () => {
                    setSelectedChapters(preset.material);
                  },
                }))}
                className="link"
                id="preset-menu"
                style={{ marginLeft: 8 }}
              >
                Apply Preset
              </DropdownMenu>
            )}
            <button
              className="link"
              style={{ position: "absolute", right: 8 }}
              onClick={() => {
                setSelectedChapters(
                  material.map((book) => book.chapters.map(() => false))
                );
              }}
            >
              Clear All
            </button>
          </div>
          <ChapterList
            allowVerseSelection={true}
            myMaterial={myMaterial}
            selected={selectedChapters}
            onChange={setSelectedChapters}
            key="current-chapters"
          />
        </>
      )}
      {tab === 2 && (
        <div className="settings-scrolling">
          <p>
            Specify any number of verse presets so you can easily switch between
            them.{" "}
            {versePresets.length > 0 &&
              "To apply a preset, choose it from the dropdown in Current Chapters."}
          </p>
          <Selection
            caption="Preset to Edit"
            value={String(presetIndex + 1)}
            onChange={(e) => {
              const index = Number.parseInt(e) - 1;
              setPresetIndex(index);
            }}
            values={{
              "0": "Create New Preset",
              ...arrayToEnum(versePresets, (preset, index) => ({
                key: String(index + 1),
                value: preset.presetName,
              })),
            }}
          />
          <StringInput
            caption="Preset Name: "
            style={{ marginTop: 10, marginBottom: 10 }}
            value={versePresets[presetIndex]?.presetName || ""}
            onChange={(value) => {
              if (presetIndex === -1) {
                setVersePresets([
                  ...versePresets,
                  {
                    presetName: value,
                    material: material.map((book) =>
                      Array(book.chapters.length).fill(false)
                    ),
                  },
                ]);
                setPresetIndex(versePresets.length);
              } else {
                const newPresets = [...versePresets];
                newPresets[presetIndex] = {
                  ...newPresets[presetIndex],
                  presetName: value,
                };
                setVersePresets(newPresets);
              }
            }}
          />
          {versePresets[presetIndex]?.presetName && (
            <>
              <div style={{ position: "relative", marginBottom: 10 }}>
                <button
                  className="link"
                  onClick={() => {
                    const newPresets = [...versePresets];
                    newPresets.push({
                      presetName: `${versePresets[presetIndex].presetName} (Copy)`,
                      material: versePresets[presetIndex].material,
                    });
                    setVersePresets(newPresets);
                    setPresetIndex(versePresets.length);
                  }}
                >
                  Duplicate Preset
                </button>
                <button
                  className="link"
                  style={{ position: "absolute", right: 0 }}
                  onClick={() => {
                    const newPresets = [...versePresets];
                    newPresets.splice(presetIndex, 1);
                    setVersePresets(newPresets);
                    setPresetIndex(-1);
                  }}
                >
                  Delete Preset
                </button>
              </div>
              <ChapterList
                allowVerseSelection
                myMaterial={myMaterial}
                selected={versePresets[presetIndex].material}
                onChange={(newMaterial) => {
                  const newPresets = [...versePresets];
                  newPresets[presetIndex] = {
                    ...newPresets[presetIndex],
                    material: newMaterial,
                  };
                  setVersePresets(newPresets);
                }}
              />
            </>
          )}
        </div>
      )}
      {tab === 3 && (
        <div className="settings-scrolling">
          <p>
            Use these options to filter down the verses you have selected. This
            will only apply to Quiz Now and Verse Cards.
          </p>
          <Selection<string>
            caption="# of Questions in Verse"
            noneText="No Filter"
            value={String(editedSettings.qsInVerseFilter ?? "")}
            values={{
              "0": "Quote Only",
              "1": "One Question",
              "2": "Two Questions",
            }}
            onChange={(value) => {
              setEditedSettings({
                ...editedSettings,
                qsInVerseFilter:
                  value === "" ? undefined : Number.parseInt(value),
              });
            }}
            spacing={false}
          />
          <p>Get in touch if you want any more custom filters!</p>
        </div>
      )}
    </div>
  );
}

interface ChapterListProps {
  allowVerseSelection: boolean;
  myMaterial?: boolean[][]; // Limits available material to only these chapters
  selected: MaterialSelection;
  onChange: (newSelected: MaterialSelection) => void;
}

/**
 * A list of chapters, that can be used for any purpose
 */
export function ChapterList(props: ChapterListProps) {
  const myMaterialUsing = useMemo(() => {
    return props.myMaterial &&
      props.myMaterial.some((book) => book.some((ch) => !!ch))
      ? props.myMaterial
      : undefined;
  }, [props.myMaterial]);

  /**
   * My material: only current year's material expanded
   * Current Chapters: all books expanded
   * @returns an array of all expanded books
   */
  const getDefaultExpanded = (material: Book[]) => {
    let booksExpanded: number[] = [];
    if (myMaterialUsing) {
      // Use my material
      myMaterialUsing.forEach((book, index) => {
        if (book.some((ch) => !!ch)) booksExpanded.push(index);
      });
    } else {
      booksExpanded = thisYearsBooks;
    }
    const currentExpanded = new Set(booksExpanded);
    const result: { book: boolean; chapters: boolean[] }[] = [];
    material.forEach((book, bookIndex) => {
      result.push({
        book: currentExpanded.has(bookIndex),
        chapters: Array(book.chapters.length).fill(false),
      });
    });
    return result;
  };

  const { material } = useAppSelector((state) => state.material);
  const [expanded, setExpanded] = useState(getDefaultExpanded(material));
  const selectedChapters = props.selected;

  const getVerseChecked = (book: number, chapter: number, verse: number) => {
    if (typeof selectedChapters[book][chapter] === "boolean") {
      return selectedChapters[book][chapter] as boolean;
    } else {
      return (selectedChapters[book][chapter] as boolean[])[verse];
    }
  };

  return (
    <div className="settings-scrolling" id="scrolling-div">
      {material.map((book, index) => (
        <div
          key={`book-check-${book.bookName}`}
          style={{
            marginTop: 5,
            display:
              myMaterialUsing && !myMaterialUsing[index].some((ch) => !!ch)
                ? "none"
                : undefined,
          }}
          id={`book-id-${index}`}
        >
          {index === 27 && (
            <p style={{ fontSize: 12 }}>
              Psalms verses and questions provided to support{" "}
              <a
                href="https://www.majorleaguequizzing.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                MLQ
              </a>
              .
            </p>
          )}
          <Row>
            <PlusMinusButton
              style={{ marginTop: 7 }}
              onClick={() => {
                // Expand or collapse the book
                const newExpanded = [...expanded];
                newExpanded[index] = {
                  ...newExpanded[index],
                  book: !newExpanded[index].book,
                };
                setExpanded(newExpanded);
              }}
              ariaLabel={expanded[index].book ? "Collapse" : "Expand"}
            >
              {expanded[index].book ? "-" : "+"}
            </PlusMinusButton>
            <Check
              checked={combineBooleans(
                selectedChapters[index],
                myMaterialUsing?.[index]
              )}
              outline={xor(selectedChapters[index], myMaterialUsing?.[index])}
              onClick={(checked) => {
                // Select the entire book
                const result = [...selectedChapters];
                if (myMaterialUsing && checked) {
                  // Select all of my material
                  result[index] = [...myMaterialUsing[index]];
                } else {
                  // Set all to true or false
                  result[index] = Array(book.chapters.length).fill(checked);
                }
                props.onChange(result);
              }}
              style={{ fontSize: 20, width: 230, marginLeft: 5 }}
            >
              {book?.bookName}
            </Check>
          </Row>
          {expanded[index].book && (
            <div style={{ marginLeft: 20 }}>
              {book.chapters.map((chapter, chapterIndex) => (
                <div
                  key={`${book.bookName}-${chapterIndex}`}
                  style={{
                    display:
                      myMaterialUsing && !myMaterialUsing[index][chapterIndex]
                        ? "none"
                        : undefined,
                  }}
                >
                  <Row
                    key={`chapter-check-${book.bookName}-${chapterIndex + 1}`}
                  >
                    {props.allowVerseSelection && (
                      <PlusMinusButton
                        style={{ marginTop: 7 }}
                        onClick={() => {
                          // Expand or collapse the book
                          const newExpanded = [...expanded];
                          newExpanded[index] = {
                            ...newExpanded[index],
                            chapters: [...newExpanded[index].chapters],
                          };
                          newExpanded[index].chapters[chapterIndex] =
                            !newExpanded[index].chapters[chapterIndex];
                          setExpanded(newExpanded);
                        }}
                        ariaLabel={
                          expanded[index].chapters[chapterIndex]
                            ? "Collapse"
                            : "Expand"
                        }
                      >
                        {expanded[index].chapters[chapterIndex] ? "-" : "+"}
                      </PlusMinusButton>
                    )}
                    <Check
                      style={{ marginLeft: 4, width: 240 }}
                      checked={selectedChapters[index][chapterIndex] === true}
                      outline={Array.isArray(
                        selectedChapters[index][chapterIndex]
                      )}
                      onClick={(checked) => {
                        // Select the chapter
                        const result = [...selectedChapters];
                        result[index] = [...result[index]];
                        result[index][chapterIndex] = checked;
                        props.onChange(result);
                      }}
                    >
                      {`${book?.bookName} ${chapterIndex + 1}`}
                    </Check>
                  </Row>
                  {expanded[index].chapters[chapterIndex] && (
                    <Row>
                      {chapter.map((_, verseIndex) => (
                        <Check
                          key={`${book.bookName}-${chapterIndex}-${verseIndex}`}
                          style={{
                            width: 35,
                            textAlign: "center",
                            padding: 0,
                          }}
                          checked={getVerseChecked(
                            index,
                            chapterIndex,
                            verseIndex
                          )}
                          onClick={(checked) => {
                            const result = [...selectedChapters];
                            result[index] = [...result[index]];
                            let newChapter = result[index][chapterIndex];
                            if (typeof newChapter === "boolean") {
                              newChapter = Array(chapter.length).fill(
                                newChapter
                              );
                            } else {
                              newChapter = [...newChapter];
                            }
                            newChapter[verseIndex] = checked;
                            if (!xor(newChapter)) {
                              // All verses are true or false, so no array is needed
                              newChapter = checked;
                            }
                            result[index][chapterIndex] = newChapter;
                            props.onChange(result);
                          }}
                        >
                          {String(verseIndex + 1)}
                        </Check>
                      ))}
                    </Row>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {props.myMaterial && (
        <p>
          If you don't see the chapter or book you're looking for, click My
          Material above to change your material selection.
        </p>
      )}
    </div>
  );
}

/**
 * Buttons to scroll straight to the current year's books
 */
export function BookQuickLinks() {
  const { material } = useAppSelector((state) => state.material);

  return (
    <div style={{ marginLeft: 10, marginBottom: 5 }}>
      {giveLinks.map((item) => (
        <button
          key={`book-expanded-${item}-link`}
          className="link"
          style={{ marginRight: 10 }}
          onClick={() => scrollToBook(item)}
        >
          {getShortenedBook(material[item]?.bookName)}
        </button>
      ))}
    </div>
  );
}
